import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { trim } from "lodash";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AsyncSelectValue } from "../../../../components/AsyncSelect";
import CustomTextArea from "../../../../components/CustomTextArea";
import Flex from "../../../../components/Flex";
import PatientSelectScroll from "@/components/PatientSelectScroll";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import StyledFormModal from "../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import Text from "../../../../components/Text";
import { INSTANT_APPOINTMENT_VALIDATION_SCHEMA } from "../../../../constants/scheduling";
import { DisclosureHost } from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useCreateInstantAppointment } from "../../../../queries/use-create-instant-appointment";
import { useInviteInstantAppointment } from "../../../../queries/use-invite-instant-appointment";
import { setCallData } from "../../../../redux/feature/join-room/JoinRoomSlice";
import { RootState, store } from "../../../../redux/store";
import { getErrorSnackbar, getSuccessSnackbar } from "../../../../utils/utils";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useRoleController } from "../../../../components/useRoleController";
import CheckIcon from "@mui/icons-material/Check";

interface InstantAppointMentModalProps {
  disclosureHost: DisclosureHost;
}

type InstantInvite = {
  note: string;
  patient?: AsyncSelectValue;
  provider?: AsyncSelectValue;
  rescheduleReason?: string;
};

const INITIAL_VALUES = {
  note: "",
  patient: undefined,
  provider: undefined,
  rescheduleReason: "",
};

const InstantAppointMentModal = ({
  disclosureHost,
}: InstantAppointMentModalProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { mutateAsync: createAppointment } = useCreateInstantAppointment();
  const { mutateAsync: inviteAppointment } = useInviteInstantAppointment();
  const showSnackbar = useSnackbar();
  const [appointment, setAppointment] = useState<any>();
  const navigate = useNavigate();
  /* A live telehealth (Zoom) call is open globally when joinRoom.opened is true. */
  const isTelehealthCallActive = useSelector(
    (state: RootState) => state.joinRoom?.opened
  );

  const copyHandler = () => {
    const patientIdParam = appointment?.patientId
      ? `&patientId=${appointment?.patientId}`
      : "";
    const appointmentIdParam = appointment?.uuid
      ? `&appointmentId=${appointment?.uuid}`
      : "";

    navigator.clipboard.writeText(
      `${window.location.origin}/public/video/preview?room=${
        appointment?.room
      }&role=${0}&providerName=${appointment?.providerName ?? ""}&patientName=${
        appointment?.patientName ?? ""
      }&startTime=${
        appointment?.startTime
      }${patientIdParam}${appointmentIdParam}`
    );

    setIsCopied((prev) => !prev);
    setTimeout(() => setIsCopied(false), 2500);
  };

  const emrInviteHandler = () => {
    inviteAppointment({
      appointmentId: appointment?.uuid,
      patientId: formik.values.patient?.value ?? "",
      providerId: formik.values.provider?.value ?? "",
      xTENANTID: localStorage.getItem("xTENANTID") ?? undefined,
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const queryClient = useQueryClient();
  const [me] = useCurrentUser();

  const meData = {
    birthDate: me?.birthDate,
    colorCode: me?.colorCode,
    label: `${me?.firstName || ""} ${me?.lastName || ""}`,
    value: me?.uuid,
  };

  const handleSubmit = (values: InstantInvite) => {
    createAppointment({
      patientId: values.patient?.value ?? "",
      providerId: values.provider?.value ?? "",
      note: values.note ? trim(values.note) : undefined,
      rescheduleReason: values.rescheduleReason
        ? trim(values.rescheduleReason)
        : undefined,
      xTENANTID: localStorage.getItem("xTENANTID") ?? undefined,
    })
      .then((res: any) => {
        queryClient.invalidateQueries({ queryKey: ["appointment-list"] });
        queryClient.invalidateQueries({ queryKey: ["future-appointment"] });
        setAppointment(res?.data?.data);
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const formik = useFormik<InstantInvite>({
    initialValues: { ...INITIAL_VALUES, provider: meData as any },
    validationSchema: INSTANT_APPOINTMENT_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  // const isFirstMount = useRef<boolean>(true);

  // const { data: appointmentData }: any = useGetAppointment({
  //   appointmentUuid: appointment?.uuid ?? undefined,
  //   providerGroupId: localStorage.getItem("realm") ?? "",
  //   disabled: !isFirstMount.current,
  // });

  // useEffect(() => {
  //   if (isFirstMount.current) {
  //     sessionStorage.setItem("patientAvatar", appointmentData?.data?.avatar);
  //     sessionStorage.setItem(
  //       "providerAvatar",
  //       appointmentData?.data?.providerAvatar
  //     );
  //     isFirstMount.current = false;
  //   }
  //   () => (isFirstMount.current = true);
  // }, [appointmentData]);

  function closeModal() {
    formik.resetForm();
    setIsCopied(false);
    disclosureHost.close();
    // isFirstMount.current = true;
    setAppointment(undefined);
    // clearSessionAvatar();
  }

  // const clearSessionAvatar = () => {
  //   sessionStorage.removeItem("patientAvatar");
  //   sessionStorage.removeItem("providerAvatar");
  // };

  const handleNavigateToPreview = () => {
    if (isTelehealthCallActive) {
      showSnackbar({
        type: "error",
        message:
          "A Zoom meeting is already in progress. Please disconnect the current meeting before starting a new Instant Appointment.",
      });
      return;
    }
    const patientIdParam = appointment?.patientId
      ? `&patientid=${appointment?.patientId}`
      : "";
    const appointmentIdParam = appointment?.uuid
      ? `&appointmentid=${appointment?.uuid}`
      : "";
    store.dispatch(
      setCallData({
        patientId: appointment?.patientId,
        appointmentId: appointment?.uuid,
        patientName: appointment?.patientName ?? "",
        providerName: appointment?.providerName ?? "",
        startTime: appointment?.startTime,
        role: 1,
      })
    );
    navigate(
      `/app/preview?room=${appointment?.room}&role=${1}&providerName=${
        appointment?.providerName ?? ""
      }&patientName=${appointment?.patientName ?? ""}&startTime=${
        appointment?.startTime
      }${patientIdParam}${appointmentIdParam}`
    );

    // navigate(getPatientVisitNoteUrl(appointment?.patientId, appointment?.uuid));
  };

  const { hasUpdate: hasUpdateInstantAppointment } = useRoleController(
    "Instant Appointment"
  );
  const { hasUpdate: hasUpdateEncounterVirtual } = useRoleController(
    "Encounter - Virtual"
  );

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Instant Appointment"
      dialogAction={
        <Flex
          width={"100%"}
          justifyContent={appointment ? "space-between" : "end"}
          gap={3}
          alignItems={"center"}
        >
          {appointment && (
            <Flex alignItems={"center"} sx={{ cursor: "pointer" }} gap={0.6}>
              {isCopied ? (
                <CheckIcon fontSize="small" color="primary" />
              ) : (
                <InsertLinkIcon fontSize="small" color="primary" />
              )}
              <Text
                color={"primary.main"}
                fontWeight={500}
                onClick={copyHandler}
              >
                {isCopied ? "Copied Appointment URL" : "Copy Appointment URL"}
              </Text>
            </Flex>
          )}

          {appointment && (
            <Flex flex={1} alignItems={"center"}>
              <Flex alignItems={"center"} sx={{ cursor: "pointer" }} gap={0.6}>
                <EmailOutlinedIcon fontSize="small" color="primary" />
                <Text
                  color={"primary.main"}
                  onClick={emrInviteHandler}
                  fontWeight={500}
                >
                  Invite via eCareHealth
                </Text>
              </Flex>
            </Flex>
          )}
          <Flex gap={2}>
            <StyledButtonV2
              label="Cancel"
              variant="outlined"
              onClick={closeModal}
            />
            {hasUpdateInstantAppointment && hasUpdateEncounterVirtual && (
              <StyledButtonV2
                label={appointment ? "Start Visit" : "Create Appointment"}
                variant="contained"
                onClick={() =>
                  appointment ? handleNavigateToPreview() : formik.submitForm()
                }
                autoFocus
                sx={{ ml: 1.2 }}
              />
            )}
          </Flex>
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "54.313rem",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={2} pt={1} mt={1}>
        <PatientSelectScroll
          label="Patient"
          required
          disabled={!!appointment}
          error={formik.touched.patient && formik.errors.patient}
          value={formik.values?.patient || null}
          onChange={(val) => formik.setFieldValue("patient", val)}
        />

        <ProviderSelectScroll
          required
          label="Provider"
          disabled={!!appointment}
          error={formik.touched.provider && formik.errors.provider}
          value={formik.values?.provider || null}
          onChange={(value) => formik.setFieldValue("provider", value)}
        />

        <CustomTextArea
          required
          // disabled={!!appointment}
          label={"Reason for Visit"}
          value={formik.values?.rescheduleReason}
          minRows={1}
          placeholder={"Type here"}
          onChange={(e) => {
            formik.setFieldValue("rescheduleReason", e.currentTarget.value);
          }}
          onBlur={formik.handleBlur}
          name="rescheduleReason"
          error={
            formik.touched.rescheduleReason &&
            (formik.errors.rescheduleReason as any)
          }
        />
        <CustomTextArea
          // disabled={!!appointment}
          label={"Note"}
          value={formik.values?.note}
          minRows={3}
          placeholder={"Type here"}
          onChange={(e) => {
            formik.setFieldValue("note", e.currentTarget.value);
          }}
        />
      </Flex>
    </StyledFormModal>
  );
};

export default InstantAppointMentModal;
